"use client";

import * as stylex from "@stylexjs/stylex";
import { Check, Users, X } from "lucide-react";
import { type ReactNode, useId, useRef } from "react";
import type { Tag } from "@/domain/schemas";
import { initials } from "@/domain/tags";
import "@/theme/tokens.stylex";
import { colors, radii, shadows } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "./Button";
import { dialogKeyDown, useFocusOnOpen } from "./dialogFocus";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import { press } from "./press";
import { tagFills } from "./tagFills";
export type WhoSheetLabels = {
  members: string;
  title: string;
  everyone: string;
  close: string;
  done: string;
};
type WhoSheetProps = {
  tags: readonly Tag[];
  selected: ReadonlySet<string>;
  labels: WhoSheetLabels;
  onToggle: (id: string) => void;
  onEveryone: () => void;
  onClose: () => void;
};
type RowProps = {
  name: string;
  checked: boolean;
  first?: boolean;
  avatar: ReactNode;
  onClick: () => void;
};
const styles = {
  row: {
    kzqmXN: "xh8yej3",
    kAzted: "xbktkl8",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1af02g3",
    k8WAf4: "xt970qd",
    kg3NbH: "xfn1xvj",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kGVxlE: "x1jidhvw",
    kMwMTN: "x1a11ngh",
    kMv6JI: "xjb2p0i",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  firstRow: {
    kGVxlE: "x1gnnqk1",
    $$css: true
  },
  avatar: {
    kmuXW: "x2lah0s",
    kzqmXN: "x1td3qas",
    kZKoxP: "x10w6t97",
    kaIpWk: "xqhrb7l",
    k1xSpc: "xrvj5dj",
    kgQiWS: "x1ku5rj1",
    kGuDYH: "xfifm61",
    k63SB2: "xfbjfs5",
    $$css: true
  }
};
export function WhoSheet({
  tags,
  selected,
  labels,
  onToggle,
  onEveryone,
  onClose
}: WhoSheetProps) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  useFocusOnOpen(dialog);
  const everyone = tags.every(tag => selected.has(tag.id));
  return <div className="xixxii4 x10a8y8t x1n327nk xrvj5dj x1ku5rj1 x1tamke2 x9f619">
      <button type="button" tabIndex={-1} aria-hidden="true" onClick={onClose} className="x10l6tqk x10a8y8t xc342km x1717udv x1ebrkjs" />
      <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={dialogKeyDown(onClose)} className="x1n2onr6 xh8yej3 x17fpy1y x1g7kexu x78zum5 xdt5ytf xb3r6kr x9f619 xwe7p7i xh540kj x1a11ngh xs0yqle x1a2a7pz">
        <div className="x2lah0s x78zum5 x1qughib x1cy8zhl x1v2ro7d x1cnzs8 x1xnnf8n xwib8y2 x110972j">
          <div className="x78zum5 xdt5ytf x17d4w8g">
            <span className="xr0iwhn xif65rj xbzybt3 xtvhhri">{labels.members}</span>
            <h2 id={titleId} className="x1ghz6dp xneq65o x1v94iar xrg9stw x1159mfc">
              {labels.title}
            </h2>
          </div>
          <IconButton icon={X} label={labels.close} onClick={onClose} />
        </div>
        <div className="x1iyjqo2 x2lwn1j x1odjw0f xish69e x1iorvi4 x4l9tsp xwib8y2">
          <Row first name={labels.everyone} checked={everyone} avatar={<span className="x2lah0s x1td3qas x10w6t97 xqhrb7l xrvj5dj x1ku5rj1 xfifm61 xfbjfs5 x5r8ks9">
                <Icon icon={Users} size={16} strokeWidth={1.75} />
              </span>} onClick={onEveryone} />
          {tags.map(tag => <Row key={tag.id} name={tag.name} checked={selected.has(tag.id)} avatar={<span {...stylex.props(styles.avatar, tagFills[tag.color])}>
                  {initials(tag.name)}
                </span>} onClick={() => onToggle(tag.id)} />)}
        </div>
        <div className="x2lah0s x78zum5 xyamay9 x4l9tsp xwxc41k x1jidhvw">
          <Button variant="primary" fullWidth onClick={onClose}>
            {labels.done}
          </Button>
        </div>
      </div>
    </div>;
}
function Row({
  name,
  checked,
  first = false,
  avatar,
  onClick
}: RowProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a styled button keeps the 56 px row, the avatar and the press scale; role and state follow the ARIA checkbox
    <button type="button" role="checkbox" aria-checked={checked} onClick={onClick} {...stylex.props(styles.row, first && styles.firstRow, press.row)}>
      <span aria-hidden="true">{avatar}</span>
      <span {...{
        0: {
          className: "x1iyjqo2 xeuugli xosj86m x1cf7cre x1yrajzq"
        },
        1: {
          className: "x1iyjqo2 xeuugli xosj86m x1yrajzq xfbjfs5"
        }
      }[!!first << 0]}>{name}</span>
      <span aria-hidden="true" {...{
        0: {
          className: "x2lah0s xgd8bvy x1fgtraw xur7f20 xrvj5dj x1ku5rj1 xmps44k xnnvkky"
        },
        1: {
          className: "x2lah0s xgd8bvy x1fgtraw xur7f20 xrvj5dj x1ku5rj1 xnnvkky xfenp3h x1gnnqk1"
        }
      }[!!checked << 0]}>
        {checked && <Icon icon={Check} size={18} strokeWidth={2.5} />}
      </span>
    </button>
  );
}